import { useEffect, useId, useMemo } from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react';
import { ModalHeader, ModalBody, ModalActions, ModalTitle } from '@clay-run/glaze/modal';
import { Text } from '@clay-run/glaze/text';
import {
  PUSH_DRAWER_PORTAL_CLASS,
  PUSH_DRAWER_POPUP_CLASS,
  registerOpenDrawer,
  unregisterOpenDrawer,
} from '../drawer-store';

/**
 * The blocking-dismissal `onOpenChange` handler, factored out of the component
 * so it is unit-testable without a DOM (`__tests__/v2-push-drawer.test.tsx`).
 *
 * ── WHY ESCAPE IS INTERCEPTED HERE AND NOWHERE ELSE ──────────────────────
 * base-ui's `useDialogRoot` passes `escapeKey: isTopmost` into its dismiss
 * handling UNCONDITIONALLY — verified against
 * `node_modules/@base-ui/react/dialog/root/useDialogRoot.js` and
 * `DialogRoot.d.ts`: there is no prop that disables it (`disablePointerDismissal`
 * only gates `outsidePress`). Reading the `reason` off `onOpenChange`'s second
 * argument and calling `eventDetails.cancel()` is therefore the only working
 * mechanism. Precedent: `mini-apps/launcher/components/CampaignDetailSidebar.tsx`.
 *
 * Any other reason falls through to `onClose`. In practice none arrives on its
 * own: `outside-press` is suppressed by `disablePointerDismissal`, and the
 * explicit close controls (`ModalHeader`'s X, a footer Cancel) call `onClose`
 * directly rather than routing through base-ui.
 */
export function createBlockingDismissalHandler(onClose: () => void) {
  return (_open: boolean, eventDetails: DialogPrimitive.Root.ChangeEventDetails): void => {
    if (eventDetails.reason === 'escape-key') {
      eventDetails.cancel();
      return;
    }
    onClose();
  };
}

interface PushDrawerProps {
  /** Panel title. Also supplies the dialog's accessible name via `ModalTitle`. */
  title: string;
  /** Optional one-line explanation under the title (prototype's `sidePanel` subtitle). */
  subtitle?: React.ReactNode;
  /** Called by the ONLY dismissal paths: the header's X and whatever the caller puts in `footer`. */
  onClose: () => void;
  /** Optional action row, rendered in Glaze's `ModalActions`. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The generic Launcher V2 push side panel — every A2 side surface builds on
 * this one primitive (plan U4; prototype's generic `sidePanel()`).
 *
 * ── IT PUSHES, IT DOES NOT OVERLAY (Decision 4) ──────────────────────────
 * Mounting this registers the drawer in `drawer-store`, which makes
 * `PlaybooksLayout` apply `MAIN_CONTENT_PUSH_CLASS` to its
 * `<main data-playbooks-main>` container: the page REFLOWS beside the panel
 * instead of being covered by it. That happens at EVERY viewport width — the
 * prototype's `@media (max-width:1400px)` overlay fallback is deliberately not
 * ported (see `drawer-store.ts`). The panel itself is full-height, which is
 * the prototype's round-4 item-6 decision (full height beat dock-below-the-
 * topbar; Spencer's explicit call).
 *
 * ── STACKING CONTRACT: Z-INDEX LIVES ON THE PORTAL, NEVER THE POPUP ──────
 * Glaze's floating primitives (`Combobox`, `ComboboxMultiple`, `Select`,
 * `Popover`) carry no z-index of their own — `isolate; z-index: auto` is the
 * house style, relying on body-portal DOM order to win. Base UI nests a child
 * portal opened INSIDE this drawer (one of those Glaze pickers) into the
 * drawer's own portal node, not `document.body`
 * (`@base-ui/react/floating-ui-react/components/FloatingPortal.js`:
 * `containerProp ?? parentPortalNode ?? document.body`). A z-index on this
 * drawer's `Popup` would therefore share a stacking context with that nested
 * child popup and paint over it — which is exactly what happened before this
 * fix. The out-ranking-app-chrome z-index instead lives on
 * `PUSH_DRAWER_PORTAL_CLASS` (`drawer-store.ts`), applied to
 * `DialogPrimitive.Portal` below; `PUSH_DRAWER_POPUP_CLASS` carries no `z-*`
 * class, and no child of this drawer may add one of its own. This is Terra's
 * own composition decision, not a Glaze convention — Glaze exposes no
 * popup/portal prop to configure this from outside.
 *
 * ── DISMISSAL IS BLOCKING, AND THAT IS DELIBERATE (Decision 9) ───────────
 * Escape does not close it. Clicking outside does not close it (there is no
 * backdrop to click, and `disablePointerDismissal` suppresses base-ui's
 * outside-press dismissal). ONLY an explicit close control does: the header's
 * X, or a button the caller renders in `footer`.
 *
 * This is uniform across EVERY drawer, including purely read-only ones — that
 * uniformity IS the decision, not an oversight of read-only panels. These
 * panels stay open through long interactions (roster click-through, merge
 * review) while the live page behind them stays interactive, so a stray
 * Escape or a click into the page is far more likely to be incidental than
 * intentional. Do not "fix" this by letting Escape through on read-only
 * panels: that reintroduces the inconsistency the decision removed, and on
 * editing panels it silently discards unsaved field state.
 *
 * The drawer is open for as long as it is mounted (`open` is hardcoded);
 * callers control visibility by conditionally rendering it, exactly as
 * `CampaignDetailSidebar` does.
 */
export function PushDrawer({ title, subtitle, onClose, footer, children }: PushDrawerProps) {
  /**
   * Push registration is keyed by a stable per-mount id and is idempotent in
   * both directions (see `drawer-store.ts`), which is what makes it
   * StrictMode-safe: dev's setup→cleanup→setup double-invoke lands on
   * `register → unregister → register` and settles with exactly one entry, and
   * two rapid open/close cycles cannot strand the layout's padding. That
   * idempotence is deliberately the mechanism INSTEAD of a lifetime ref — no
   * `mounted`/`initialized` ref exists here to get stuck after the first
   * mount.
   */
  const drawerId = useId();
  useEffect(() => {
    registerOpenDrawer(drawerId);
    return () => unregisterOpenDrawer(drawerId);
  }, [drawerId]);

  const handleOpenChange = useMemo(() => createBlockingDismissalHandler(onClose), [onClose]);

  return (
    <DialogPrimitive.Root open onOpenChange={handleOpenChange} modal={false} disablePointerDismissal>
      {/* Twin fix: mini-apps/launcher/components/CampaignDetailSidebar.tsx
          duplicates this same Portal/Popup z-index split (launcher may not
          import from playbooks). Keep both in sync by hand. */}
      <DialogPrimitive.Portal className={PUSH_DRAWER_PORTAL_CLASS}>
        {/* No `DialogPrimitive.Backdrop`: a scrim would both dim and block the
            page this panel is deliberately sitting beside. */}
        <DialogPrimitive.Popup
          /* `focus:outline-0`: base-ui focuses the popup itself on open, and the
             UA focus ring on a full-height panel reads as an error state. Same
             treatment Glaze gives its own dialog container (`dialog.tsx`);
             interactive children keep their own focus rings. */
          className={PUSH_DRAWER_POPUP_CLASS}
        >
          <ModalHeader onClose={onClose}>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <ModalTitle>{title}</ModalTitle>
              {subtitle && (
                <Text size="xs" variant="tertiary">
                  {subtitle}
                </Text>
              )}
            </div>
          </ModalHeader>

          {/* `overscroll-contain`: a wheel that exhausts the panel's own scroll
              must not chain into the page behind it (prototype round-3 item 4).
              The page itself stays scrollable — this is not a modal. */}
          <ModalBody
            padded={false}
            className="flex flex-1 flex-col gap-3.5 overflow-y-auto overscroll-contain p-4"
          >
            {children}
          </ModalBody>

          {footer && <ModalActions className="flex-wrap">{footer}</ModalActions>}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
